/**
 * Profil (konto, familie, notifikationer, logout) og familieforbindelsen
 * til Conventus.
 *
 * Notifikationssiden er ærlig om, hvad der faktisk sendes: kun
 * beskednotifikationer og testnotifikationen er implementeret (§11).
 */

import { useEffect, useState } from 'react';
import { api, departmentLabel } from '../lib/api.js';
import { Link } from '../lib/router.js';
import {
  ActionButton,
  AsyncSection,
  Badge,
  EmptyState,
  Field,
  Notice,
  useAction,
  useAsync,
  Meta,
  PageHead,
  SectionHead,
} from '../components/ui.js';
import { Icon } from '../components/icons.js';
import {
  isRole,
  NOTIFICATION_PREFERENCE_HINTS,
  NOTIFICATION_PREFERENCE_LABELS,
  ROLE_DESCRIPTIONS,
  roleLabel,
} from '../../shared/domain.js';

interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: string;
  departments: string[];
  hasWorkspace: boolean;
  hasFamily: boolean;
}



type PreferenceKey = keyof typeof NOTIFICATION_PREFERENCE_LABELS;

interface PreferencesData {
  preferences: Record<string, boolean>;
  subscriptions: Array<{ id: string; deviceLabel: string; active: boolean; disabledReason: string }>;
  hints: Record<string, string>;
  /** De seneste udsendelser til kontoen. Ærlighed frem for et løfte. */
  recent: Array<{ kind: string; sentAt: string; outcome: string }>;
  note: string;
}

/* ------------------------------------------------------------------ */
/* Profil                                                              */
/* ------------------------------------------------------------------ */

export function ProfilePage({ user }: { user: SessionUser }): JSX.Element {
  const family = useAsync<{ links: Array<{ personKey: string; name: string; relation: string; source: string }> }>(
    () => api.get('/api/member-login'),
  );
  const prefs = useAsync<PreferencesData>(() => api.get('/api/notification-preferences'));
  const pushConfig = useAsync<{ enabled: boolean; publicKey: string }>(() => api.get('/api/push/config'));

  return (
    <div className="stack">
      <PageHead title="Profil" />

      <section className="section" aria-labelledby="konto">
        <SectionHead title="Konto" icon="profile" id="konto" />
        <div className="card">
          <div className="meta">
            <Meta icon="profile" label="Navn">
              {/* Kontoen kan være uden navn, indtil familien er forbundet —
                  så skal der stå noget frem for ingenting. */}
              {user.name.trim() || <span className="muted">Ikke angivet endnu</span>}
            </Meta>
            <Meta icon="message" label="Mail">
              {user.email}
            </Meta>
          </div>
          <p className="tiny muted mt-3">
            Navn, kontaktoplysninger, indmeldelse og kontingent vedligeholdes i Conventus — ikke
            her.
          </p>
        </div>
      </section>

      <section className="section" aria-labelledby="familie">
        <SectionHead
          title="Familie"
          icon="family"
          id="familie"
          action={
            <Link className="btn btn--secondary btn--small" to="/medlemslogin">
              Ret tilknytning
            </Link>
          }
        />
        <div className="card">
        <AsyncSection state={family} skeletonLines={2}>
          {(data) =>
            data.links.length === 0 ? (
              <EmptyState title="Ingen profiler forbundet" icon="family">
                Forbind din konto med foreningens medlemsregister for at se hold og kalender.
              </EmptyState>
            ) : (
              <ul className="rows">
                {data.links.map((link) => (
                  <li key={link.personKey} className="row row--between">
                    <span className="small">{link.name}</span>
                    <Badge>{link.relation === 'self' ? 'Dig selv' : 'Barn/familie'}</Badge>
                  </li>
                ))}
              </ul>
            )
          }
        </AsyncSection>
        </div>
      </section>

      <NotificationsSection prefs={prefs} pushEnabled={pushConfig.data?.enabled ?? false} publicKey={pushConfig.data?.publicKey ?? ''} />

      {/*
        ROLLER OG ADGANG som sit eget afsnit.
        Rollen stod før som en linje inde under «Konto» ved siden af navn og
        mail. Men en rolle er ikke en kontaktoplysning — den afgør, hvad man
        kan se og gøre, og det er værd at kunne slå op uden at gætte.
      */}
      <section className="section" aria-labelledby="roller">
        <SectionHead title="Roller og adgang" icon="roles" id="roller" />
        <div className="card">
          <div className="meta">
            <Meta icon="roles" label="Din rolle">
              <strong>{roleLabel(user.role)}</strong>
            </Meta>
            {user.departments.length > 0 ? (
              <Meta icon="teams" label="Afdelinger">
                {user.departments.map(departmentLabel).join(', ')}
              </Meta>
            ) : null}
          </div>
          <p className="small muted mt-3">
            {/*
              Beskrivelsen kommer fra domænelaget og ikke fra en kæde af
              hvis-sætninger her på siden. Den kæde fandtes engang, kendte kun
              to af foreningens fem roller, og kaldte derfor administratoren
              for instruktør.
            */}
            {isRole(user.role) ? ROLE_DESCRIPTIONS[user.role] : 'Appen kender ikke denne rolle.'}
          </p>
          <p className="tiny muted mt-2">
            Roller tildeles af bestyrelsen. Skal din adgang laves om, er det
            dem, du skal fat i — den kan ikke ændres her.
          </p>
        </div>
      </section>

      {/*
        HJÆLP OG PRIVATLIV.
        Det, folk faktisk spørger om, når de får en foreningsapp: hvor kommer
        mine oplysninger fra, hvem kan se dem, og hvem retter dem. Det stod
        spredt rundt i appen før — én linje under «Konto», én i bunden af
        beskederne. Nu står det ét sted, man kan henvise til.
      */}
      <section className="section" aria-labelledby="hjaelp">
        <SectionHead title="Hjælp og privatliv" icon="info" id="hjaelp" />
        <div className="card">
          <div className="meta">
            <Meta icon="conventus" label="Hvor dine oplysninger kommer fra">
              Navn, mail, hold og tilmeldinger hentes fra Conventus. Appen
              læser derfra og skriver aldrig tilbage.
            </Meta>
            <Meta icon="family" label="Hvem kan se hvad">
              Deltagerlisten på et hold vises kun for holdets instruktører og
              for bestyrelsen. Som medlem ser du din egen familie.
            </Meta>
            <Meta icon="info" label="Hele teksten">
              <Link to="/privatliv">Sådan behandler vi dine oplysninger</Link>
            </Meta>
            <Meta icon="profile" label="Rettelser">
              Skal navn, mail eller tilmelding rettes, sker det i Conventus.
              Retter du det her, ville det blive skrevet over ved næste
              hentning.
            </Meta>
          </div>
        </div>
      </section>

      <RightsSection />

      <section className="section" aria-labelledby="log-ud">
        <SectionHead title="Log ud" icon="logout" id="log-ud" />
        <div className="card">
        <p className="small muted">
          Du logges ud på denne enhed. Gemte notifikationsindstillinger bevares.
        </p>
        <p className="mt-3">
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => {
              // POST, ikke et link: så virker udlogningen uanset loginmetode,
              // og den kan ikke udløses af et fremmed websted (CSRF).
              void api.post('/api/signout').finally(() => {
                window.location.href = '/';
              });
            }}
          >
            <Icon name="logout" size={18} />
            Log ud
          </button>
        </p>
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Notifikationer                                                      */
/* ------------------------------------------------------------------ */

function NotificationsSection({
  prefs,
  pushEnabled,
  publicKey,
}: {
  prefs: ReturnType<typeof useAsync<PreferencesData>>;
  pushEnabled: boolean;
  publicKey: string;
}): JSX.Element {
  const action = useAction();
  const [permission, setPermission] = useState<string>('default');
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    const ok =
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    setSupported(ok);
    if (ok) setPermission(Notification.permission);
  }, []);

  const enablePush = (): void => {
    void action.run(async () => {
      if (!supported) throw new Error('unsupported');
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') {
        throw Object.assign(new Error('denied'), {
          name: 'ApiError',
          message:
            'Du afviste notifikationer. Du kan slå dem til igen i browserens indstillinger for denne side.',
        });
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = subscription.toJSON() as { endpoint?: string; keys?: Record<string, string> };
      await api.post('/api/push/subscribe', {
        endpoint: json.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
        deviceLabel: navigator.platform || 'Denne enhed',
      });
      action.setSuccess('Notifikationer er slået til på denne enhed.');
      prefs.reload();
    });
  };

  const disablePush = (): void => {
    void action.run(async () => {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await api.post('/api/push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
      action.setSuccess('Notifikationer er slået fra på denne enhed.');
      prefs.reload();
    });
  };

  const sendTest = (): void => {
    void action.run(async () => {
      const result = await api.post<{ result: { accepted: number; attempted: number; note: string } }>(
        '/api/push/test',
      );
      action.setSuccess(
        `Pushtjenesten accepterede ${result.result.accepted} af ${result.result.attempted} forsøg. ${result.result.note}`,
      );
    });
  };

  const savePreference = (key: string, value: boolean): void => {
    void action.run(async () => {
      const current = prefs.data?.preferences ?? {};
      await api.put('/api/notification-preferences', { ...current, [key]: value });
      prefs.reload();
    });
  };

  return (
    <section className="section" aria-labelledby="notifikationer">
      <SectionHead title="Notifikationer" icon="bell" id="notifikationer" />
      <div className="card">
      {!pushEnabled ? (
        <div>
          <Notice kind="warning">
            Pushnotifikationer er ikke konfigureret på denne server endnu. Beskeder vises stadig i
            din indbakke.
          </Notice>
        </div>
      ) : !supported ? (
        <div>
          <Notice kind="warning">
            Denne browser understøtter ikke pushnotifikationer. På iPhone skal du først lægge appen
            på hjemmeskærmen: tryk «Del» og derefter «Føj til hjemmeskærm».
          </Notice>
        </div>
      ) : (
        <div className="stack stack--tight">
          {action.error ? <Notice kind="error">{action.error}</Notice> : null}
          {action.success ? <Notice kind="success">{action.success}</Notice> : null}
          <div className="btn-group">
            {permission === 'granted' ? (
              <>
                <ActionButton className="btn btn--secondary" pending={action.pending} onClick={sendTest}>
                  <Icon name="bell" size={17} />
                  Send testnotifikation
                </ActionButton>
                <ActionButton className="btn btn--danger" pending={action.pending} onClick={disablePush}>
                  Slå fra på denne enhed
                </ActionButton>
              </>
            ) : (
              <ActionButton pending={action.pending} onClick={enablePush}>
                Aktivér notifikationer
              </ActionButton>
            )}
          </div>
          {permission === 'denied' ? (
            <Notice kind="warning">
              Notifikationer er blokeret for denne side i browserens indstillinger. Slå dem til der
              først.
            </Notice>
          ) : null}
        </div>
      )}

      <AsyncSection state={prefs} skeletonLines={3}>
        {(data) => (
          <div className="mt-4">
            <h3>Hvad vil du have besked om?</h3>
            <div className="stack stack--tight mt-2">
              {Object.entries(data.preferences).map(([key, value]) => (
                <label key={key} className="checkbox">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => savePreference(key, (e.target as HTMLInputElement).checked)}
                  />
                  <span>
                    <span className="small">
                      {NOTIFICATION_PREFERENCE_LABELS[key as PreferenceKey] ?? key}
                    </span>
                    <span className="tiny muted" style={{ display: 'block' }}>
                      {data.hints?.[key] ?? NOTIFICATION_PREFERENCE_HINTS[key as PreferenceKey]}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <p className="tiny muted mt-3">
              {data.note} En vigtig-markering tilsidesætter ikke et fravalg: du skal både have
              slået «Vigtige beskeder» og den relevante kategori til.
            </p>
          </div>
        )}
      </AsyncSection>
      </div>
    </section>
  );
}

/** Konverterer VAPID-nøglen til det format, PushManager kræver. */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalised);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

/* ------------------------------------------------------------------ */
/* Familieforbindelse                                                  */
/* ------------------------------------------------------------------ */

interface Candidate {
  memberId: string;
  name: string;
  emailMasked: string;
}

interface MemberLoginData {
  available: boolean;
  reason?: string;
  links: Array<{ personKey: string; name: string; relation: string }>;
  suggestions?: Candidate[];
  verifiedEmail?: string;
  note?: string;
  emptyStateNote?: string | null;
}

/**
 * Forbind kontoen med medlemsregisteret — i ÉT skridt.
 *
 * HVORFOR
 * Mailadressen er allerede bekræftet, da brugeren loggede ind på den. Så der
 * er ingen grund til først at bede ham trykke «find mine profiler» og
 * derefter vælge. Serveren sender forslagene med det samme, og skærmen
 * spørger direkte: «Er det din familie?»
 *
 * Selve bekræftelsen går stadig gennem en kontobundet engangschallenge på
 * serveren, så listen af profiler, der kan vælges, låses serverside. Det
 * skridt er bare usynligt for brugeren nu.
 */
export function MemberLoginPage({ userName = '' }: { userName?: string }): JSX.Element {
  const state = useAsync<MemberLoginData>(() => api.get('/api/member-login'));
  const action = useAction();
  const [selection, setSelection] = useState<Record<string, Relation> | null>(null);

  const suggestions = state.data?.suggestions ?? [];
  const links = state.data?.links ?? [];

  // Førstegangsvalg: alle foreslåede profiler markeres som familie, og den
  // profil, der bærer kontoens eget navn, markeres som «mig». Brugeren
  // behøver altså kun at rette, hvis vi gætter forkert.
  const effective: Record<string, Relation> =
    selection ??
    Object.fromEntries(
      suggestions.map((c) => [
        c.memberId,
        defaultRelation(c, suggestions, userName, links) as Relation,
      ]),
    );

  const setRelation = (memberId: string, relation: Relation): void => {
    setSelection({ ...effective, [memberId]: relation });
  };

  const selectedCount = Object.values(effective).filter((r) => r !== 'none').length;

  const confirm = (): void => {
    void action.run(async () => {
      // Challengen hentes først her, ved brugerens ene tryk. Den bruges med
      // det samme, så et sideophold ikke efterlader ubrugte challenges.
      const started = await api.post<{ token: string; candidates: Candidate[] }>(
        '/api/member-login/start',
      );
      const allowed = new Set(started.candidates.map((c) => c.memberId));
      const selections = Object.entries(effective)
        .filter(([memberId, relation]) => relation !== 'none' && allowed.has(memberId))
        .map(([memberId, relation]) => ({ memberId, relation }));

      if (selections.length === 0) {
        throw new Error(
          'Profilerne kunne ikke bekræftes — de er ikke længere i medlemsregisteret. Genindlæs siden.',
        );
      }

      await api.post('/api/member-login/confirm', { token: started.token, selections });
      action.setSuccess(
        selections.length === 1
          ? 'Profilen er forbundet. Hold og kalender er opdateret.'
          : `${selections.length} profiler er forbundet. Hold og kalender er opdateret.`,
      );
      setSelection(null);
      state.reload();
    });
  };

  return (
    <div className="stack">
      <PageHead
        title="Din familie"
        eyebrow="Medlemslogin"
        eyebrowIcon="family"
        sub="Forbind din konto med foreningens medlemsregister"
      />
      {action.error ? <Notice kind="error">{action.error}</Notice> : null}
      {action.success ? <Notice kind="success">{action.success}</Notice> : null}

      <AsyncSection state={state}>
        {(data) =>
          !data.available ? (
            <Notice kind="warning">{data.reason}</Notice>
          ) : suggestions.length === 0 ? (
            <>
              <section className="card">
                <EmptyState title="Vi fandt ingen profiler på din mailadresse" icon="search">
                  {data.emptyStateNote ??
                    'Der er ingen profiler i medlemsregisteret med din mailadresse. Kontakt bestyrelsen, hvis du mener, det er en fejl.'}
                </EmptyState>
                <p className="tiny muted mt-3">
                  Du er logget ind som <strong>{data.verifiedEmail}</strong>. Er familien meldt ind
                  med en anden mailadresse, skal den rettes i Conventus — så virker det med det
                  samme her.
                </p>
              </section>
              {links.length > 0 ? <ConnectedList links={links} /> : null}
            </>
          ) : (
            <>
              <section className="card">
                <h2>Er det din familie?</h2>
                <p className="small muted mt-2">
                  Vi fandt {suggestions.length === 1 ? 'denne profil' : `disse ${suggestions.length} profiler`} i
                  medlemsregisteret på <strong>{data.verifiedEmail}</strong> — den adresse, du lige
                  har logget ind på. Ret hvis noget ikke passer, og bekræft.
                </p>

                <div className="stack stack--tight mt-4">
                  {suggestions.map((candidate) => (
                    <div key={candidate.memberId} className="row row--between">
                      <div>
                        <div className="small">
                          <strong>{candidate.name}</strong>
                        </div>
                        <div className="tiny muted">{candidate.emailMasked}</div>
                      </div>
                      <div className="seg" role="group" aria-label={`Relation for ${candidate.name}`}>
                        {RELATIONS.map((relation) => (
                          <button
                            key={relation}
                            type="button"
                            aria-pressed={effective[candidate.memberId] === relation}
                            onClick={() => setRelation(candidate.memberId, relation)}
                          >
                            {RELATION_LABELS[relation]}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4">
                  <ActionButton
                    className="btn btn--full"
                    pending={action.pending}
                    disabled={selectedCount === 0}
                    onClick={confirm}
                  >
                    {selectedCount === suggestions.length
                      ? 'Ja, det er min familie'
                      : selectedCount === 0
                        ? 'Vælg mindst én profil'
                        : `Bekræft ${selectedCount} ${selectedCount === 1 ? 'profil' : 'profiler'}`}
                  </ActionButton>
                </div>

                <p className="tiny muted mt-3">
                  Vi søger kun på den mailadresse, du er logget ind med. En bekræftelse erstatter
                  din nuværende tilknytning — du kan altid vende tilbage hertil og rette den.
                </p>
              </section>

              {links.length > 0 ? <ConnectedList links={links} /> : null}
            </>
          )
        }
      </AsyncSection>
    </div>
  );
}

type Relation = 'none' | 'self' | 'child';
const RELATIONS: readonly Relation[] = ['self', 'child', 'none'];
const RELATION_LABELS: Record<Relation, string> = {
  self: 'Mig',
  child: 'Familie',
  none: 'Ikke min',
};

/**
 * Gætter en fornuftig startværdi, så det almindelige tilfælde kun kræver ét
 * tryk. Gættet er konservativt: vi markerer kun «mig», når navnet er kendt
 * og passer præcist — ellers bliver profilen til familie, hvilket giver
 * nøjagtig samme adgang.
 */
function defaultRelation(
  candidate: Candidate,
  all: readonly Candidate[],
  userName: string,
  links: ReadonlyArray<{ name: string; relation: string }>,
): Relation {
  const existing = links.find((l) => l.name === candidate.name);
  if (existing) return existing.relation === 'self' ? 'self' : 'child';

  const name = userName.trim().toLowerCase();
  if (name && candidate.name.trim().toLowerCase() === name) {
    const alreadySelf = all.some(
      (other) => other !== candidate && other.name.trim().toLowerCase() === name,
    );
    if (!alreadySelf) return 'self';
  }
  return 'child';
}

function ConnectedList({
  links,
}: {
  links: ReadonlyArray<{ personKey: string; name: string; relation: string }>;
}): JSX.Element {
  return (
    <section className="section" aria-labelledby="forbundet">
      <SectionHead title="Forbundet i dag" icon="family" id="forbundet" />
      <div className="card">
      <ul className="rows">
        {links.map((link) => (
          <li key={link.personKey} className="row row--between">
            <span className="small">{link.name}</span>
            <Badge>{link.relation === 'self' ? 'Dig selv' : 'Barn/familie'}</Badge>
          </li>
        ))}
      </ul>
      </div>
    </section>
  );
}

export { Field };

/* ------------------------------------------------------------------ */
/* Dine rettigheder: indsigt og sletning                               */
/* ------------------------------------------------------------------ */

/**
 * RET TIL INDSIGT OG RET TIL SLETNING.
 *
 * Begge dele gælder, uanset om der er en knap i appen. Her ER der en knap,
 * så retten ikke afhænger af, at nogen i bestyrelsen har tid til at gøre det
 * i hånden — og så medlemmet ikke skal skrive til nogen for at få det.
 *
 * DEN VIGTIGSTE SÆTNING PÅ HELE SIDEN står ved sletningen: at slette sin
 * adgang til appen er IKKE at melde sig ud af foreningen. Medlemskabet ligger
 * i Conventus. Uden den sætning ville nogen slette kontoen i den tro, at de
 * havde meldt deres barn fra holdet — og opdage det, når kontingentet kom.
 */
function RightsSection(): JSX.Element {
  const action = useAction();
  const [forhaandsvisning, setForhaandsvisning] = useState<Record<string, number> | null>(null);
  const [bekraeftet, setBekraeftet] = useState('');

  const hentData = (): void => {
    void action.run(async () => {
      const data = await api.get<Record<string, unknown>>('/api/mine-data');
      /*
        Filen laves i browseren af det, serveren lige har sendt. Den bliver
        ikke liggende noget sted — hverken på serveren eller i en mail.
      */
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mine-data-skals-gf.json';
      a.click();
      URL.revokeObjectURL(url);
      action.setSuccess('Filen er hentet til din enhed.');
    });
  };

  const spoergOmSletning = (): void => {
    void action.run(async () => {
      setForhaandsvisning(await api.get<Record<string, number>>('/api/min-konto/slet'));
    });
  };

  const slet = (): void => {
    void action.run(async () => {
      await api.del('/api/min-konto');
      window.location.href = '/';
    });
  };

  return (
    <section className="section" aria-labelledby="rettigheder">
      <SectionHead title="Dine data" icon="profile" id="rettigheder" />
      <div className="card stack stack--tight">
        {action.error ? <Notice kind="error">{action.error}</Notice> : null}
        {action.success ? <Notice kind="success">{action.success}</Notice> : null}

        <p className="small">
          Du kan altid hente det, appen har om dig, og du kan slette din adgang igen.
        </p>

        <p>
          <ActionButton className="btn btn--secondary" pending={action.pending} onClick={hentData}>
            <Icon name="profile" size={17} />
            Hent mine data
          </ActionButton>
        </p>

        <hr style={{ border: 0, borderTop: '1px solid var(--border)', margin: '4px 0' }} />

        {forhaandsvisning ? (
          <>
            <Notice kind="warning">
              <strong>Er du sikker?</strong>
              <p className="small mt-1">
                Din adgang til appen forsvinder: {forhaandsvisning.familietilknytninger}{' '}
                familietilknytning(er), {forhaandsvisning.hold} holdtilknytning(er),{' '}
                {forhaandsvisning.afbud} afbud og {forhaandsvisning.frivilligtilmeldinger}{' '}
                frivilligtilmelding(er).
              </p>
              {forhaandsvisning.beskederDuHarSendt > 0 ||
              forhaandsvisning.fremmoedeDuHarRegistreret > 0 ? (
                <p className="small mt-2">
                  Beskeder, du har sendt til andre ({forhaandsvisning.beskederDuHarSendt}), og
                  fremmøde, du har registreret ({forhaandsvisning.fremmoedeDuHarRegistreret}),
                  bliver stående — de er også andres. Dit navn fjernes fra dem.
                </p>
              ) : null}
              <p className="small mt-2">
                <strong>Du melder dig ikke ud af foreningen.</strong> Medlemskabet ligger i
                Conventus. Skal barnet meldes fra et hold, sker det dér.
              </p>
              {/*
                Det står HER og ikke kun i politikken. Den, der trykker på
                knappen, læser den her boks — ikke nødvendigvis en side, hun
                skal klikke sig ind på bagefter.
              */}
              <p className="tiny mt-2">
                Der tages en sikkerhedskopi af databasen hvert døgn, og de syv nyeste gemmes.
                Dine oplysninger kan derfor ligge i en kopi i op til en uge endnu, før de er
                helt væk.
              </p>
            </Notice>

            {/*
              Man skal skrive ordet. Ikke for at genere, men fordi det her er
              det eneste sted i appen, hvor et fejltryk ikke kan fortrydes.
            */}
            <Field
              label="Skriv SLET for at bekræfte"
              htmlFor="slet-bekraeft"
              hint="Handlingen kan ikke fortrydes."
            >
              <input
                id="slet-bekraeft"
                type="text"
                value={bekraeftet}
                onChange={(e) => setBekraeftet((e.target as HTMLInputElement).value)}
              />
            </Field>
            <div className="btn-group">
              <ActionButton
                className="btn btn--danger"
                pending={action.pending}
                disabled={bekraeftet.trim().toUpperCase() !== 'SLET'}
                onClick={slet}
              >
                Slet min adgang
              </ActionButton>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => {
                  setForhaandsvisning(null);
                  setBekraeftet('');
                }}
              >
                Fortryd
              </button>
            </div>
          </>
        ) : (
          <p>
            <ActionButton
              className="btn btn--ghost"
              pending={action.pending}
              onClick={spoergOmSletning}
            >
              Slet min adgang til appen
            </ActionButton>
          </p>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Privatlivspolitik                                                   */
/* ------------------------------------------------------------------ */

/**
 * OPLYSNINGSPLIGTEN.
 *
 * Teksten her er skrevet ud fra, hvad appen FAKTISK gør — ikke ud fra en
 * skabelon. Hvert tal og hver periode svarer til noget i koden:
 * opbevaringsperioderne er de konstanter, oprydningen bruger, og listen over
 * hvad der gemmes, er gennemgået tabel for tabel.
 *
 * VEDTAGET AF BESTYRELSEN 5. OKTOBER 2026. Udkastbåndet, der stod her,
 * indtil da, er fjernet samme dag — og datoen står nederst på siden.
 *
 * TIL DEN, DER ÆNDRER TEKSTEN HEREFTER: en politik er vedtaget i en bestemt
 * ordlyd. Retter du noget væsentligt — hvad der gemmes, hvor længe, hvem det
 * deles med — så er den vedtagne udgave ikke længere den, medlemmerne ser.
 * Så skal bestyrelsen vedtage den igen, datoen nederst rettes, og dokumentet
 * hentes ud af appen på ny med `scripts/politik-udtraek.ts`. Sprogrettelser
 * og slåfejl kan naturligvis rettes uden.
 */
export function PrivacyPage(): JSX.Element {
  return (
    <div className="stack">
      <PageHead
        title="Sådan behandler vi dine oplysninger"
        eyebrow="Privatliv"
        eyebrowIcon="info"
        sub="Skals Gymnastikforening"
      />

      <section className="card stack stack--tight">
        <h2>Hvem er ansvarlig</h2>
        <p className="small">
          Skals Gymnastikforening er dataansvarlig for de oplysninger, appen behandler. Har du
          spørgsmål, eller vil du gøre brug af dine rettigheder, kan du skrive til foreningens
          kontaktperson for persondata:
        </p>
        {/*
          EN FORENINGSADRESSE, IKKE EN PRIVAT.
          Her stod først kontaktpersonens egen Gmail. To grunde til at skifte:
          adressen overlever, at rollen gives videre — ellers skal appen,
          fortegnelsen og alt trykt materiale rettes den dag — og en privat
          adresse hører ikke hjemme på en side, alle foreningens familier ser.

          Navnet står stadig, fordi et medlem skal vide, hvem der læser med.
        */}
        <p className="small">
          <a href="mailto:persondata@skalsgf.dk">persondata@skalsgf.dk</a>
          <br />
          <span className="muted">Passes af Rune Tolstrup Jørgensen</span>
        </p>
        <p className="tiny muted">
          Du får svar inden for en måned, som forordningen kræver. Er du ikke enig i svaret,
          kan du klage til Datatilsynet.
        </p>
      </section>

      <section className="card stack stack--tight">
        <h2>Hvad vi gemmer — og hvorfor</h2>
        <div className="meta">
          <Meta icon="profile" label="Din konto">
            Navn og mailadresse. Bruges til at logge dig ind og til at vise, hvem der har sendt
            en besked.
          </Meta>
          <Meta icon="family" label="Familietilknytning">
            Hvilke medlemmer i foreningen du er forælder til eller selv er. Bruges til at vise
            de rigtige hold og træninger.
          </Meta>
          <Meta icon="calendar" label="Kalender og afbud">
            Afbud du melder, med årsag og en eventuel bemærkning.
          </Meta>
          <Meta icon="attendance" label="Fremmøde">
            Instruktøren kan registrere, hvem der var til træning.
          </Meta>
          <Meta icon="message" label="Beskeder">
            Beskeder til dig og dine svar til afsenderen.
          </Meta>
          <Meta icon="volunteer" label="Frivilligopgaver">
            Hvilke opgaver du har meldt dig til.
          </Meta>
        </div>
        <p className="tiny muted mt-2">
          Vi gemmer <strong>ikke</strong> fødselsdato, adresse, telefonnummer eller
          betalingsoplysninger. De ligger i Conventus, som foreningen bruger til medlemsstyring.
        </p>
      </section>

      {/*
        RETSGRUNDLAGET SKAL STÅ HER.
        Artikel 13, stk. 1, litra c kræver, at et medlem får at vide, PÅ
        HVILKET GRUNDLAG foreningen behandler oplysningerne. Afsnittet manglede,
        mens fortegnelsen hele tiden har svaret rigtigt — og så kan de to
        dokumenter ikke godkendes som ét sæt.

        Det er IKKE samtykke, og det er værd at forstå hvorfor: et samtykke kan
        trækkes tilbage, og gør et medlem det, står foreningen med en
        medlemsforpligtelse, den ikke længere må bruge appen til at opfylde.
        Grundlaget er medlemsforholdet selv.
      */}
      <section className="card stack stack--tight">
        <h2>Hvorfor vi må gemme det</h2>
        <p className="small">
          Foreningen behandler oplysningerne, fordi det er nødvendigt for at opfylde{' '}
          <strong>medlemsforholdet</strong> — databeskyttelsesforordningens artikel 6, stk. 1,
          litra b. Du skal kunne se dit holds træninger, melde afbud og få besked, når noget
          bliver flyttet.
        </p>
        <p className="small">
          De dele, der ikke følger direkte af medlemskabet — frivilligopgaver, tilmelding til
          arrangementer og driften af selve appen — hviler på foreningens{' '}
          <strong>legitime interesse</strong> i at kunne drive sit virke, artikel 6, stk. 1,
          litra f.
        </p>
        <p className="tiny muted">
          Grundlaget er <strong>ikke samtykke</strong>. Det er et bevidst valg: et samtykke kan
          trækkes tilbage, og så ville foreningen stå med en forpligtelse over for et medlem,
          den ikke længere måtte opfylde. Du kan til gengæld altid slette din adgang til appen —
          se nedenfor — og dit medlemskab af foreningen fortsætter uændret.
        </p>
      </section>

      <section className="card stack stack--tight">
        <h2>Hvor længe</h2>
        <ul className="small" style={{ margin: '0 0 0 18px', padding: 0 }}>
          <li>
            Fremmøde, afbud, beskeder, arrangementer og frivilligopgaver:{' '}
            <strong>to sæsoner</strong>, derefter slettes de automatisk.
          </li>
          <li>Hvornår appen er brugt (til at se, om den virker): 400 dage.</li>
          <li>Log over udsendte notifikationer: 120 dage.</li>
          <li>Login-links: udløber efter kort tid og slettes.</li>
          <li>Sikkerhedskopier af databasen: <strong>7 dage</strong>.</li>
          <li>Din konto: indtil du selv sletter den, eller foreningen gør det.</li>
        </ul>
      </section>

      {/*
        SIKKERHEDSKOPIERNE HØRTE IKKE MED, DA TEKSTEN BLEV SKREVET — de fandtes
        ikke endnu. Da de kom til, lovede afsnittet om sletning pludselig noget,
        appen ikke holdt: en konto forsvinder med det samme, men navnet ligger
        i kopierne i op til en uge endnu.

        Det er lovligt og fuldstændig almindeligt. Det, der ikke er i orden, er
        at lade være med at skrive det. Et medlem, der trykker «slet», skal
        kunne læse, hvad der så faktisk sker — ellers er samtalen den dag,
        nogen opdager det, en anden og værre samtale.
      */}
      <section className="card stack stack--tight">
        <h2>Sikkerhedskopier</h2>
        <p className="small">
          Der bliver taget en kopi af databasen én gang i døgnet, så foreningens data kan
          gendannes, hvis noget går galt. De syv nyeste kopier gemmes; ældre slettes automatisk.
        </p>
        <p className="small">
          Det betyder, at oplysninger, du har fået slettet, stadig kan findes i en kopi i op til
          <strong> syv dage</strong> bagefter. Kopierne bruges udelukkende til at gendanne
          databasen efter et nedbrud — de bliver ikke set igennem eller brugt til noget andet, og
          kun foreningens administrator har adgang til dem.
        </p>
      </section>

      <section className="card stack stack--tight">
        <h2>Hvem kan se hvad</h2>
        <p className="small">
          Deltagerlisten på et hold ses kun af holdets instruktører og af bestyrelsen. Som medlem
          ser du din egen familie. Et svar på en besked går <strong>kun</strong> til afsenderen —
          ingen andre modtagere ser det.
        </p>
        {/*
          PRÆCIST OM LEVERANDØRERNE.
          Her stod før «Railway (serveren, placeret i EU)». Det var for kort.
          Databasen ligger i Railways europæiske region, men Railway er et
          amerikansk selskab, og deres egen databehandleraftale siger, at
          hovedbehandlingen sker i USA. Der ER altså en overførsel til et
          tredjeland, og den hviler på EU-Kommissionens standardbestemmelser.
          Det er helt almindeligt og lovligt — men det skal stå rigtigt, ikke
          nemt.
        */}
        <p className="small">
          Oplysningerne deles ikke med andre end de to leverandører, der driver appen for
          foreningen: <strong>Railway</strong> (serveren og databasen) og <strong>Resend</strong>{' '}
          (afsendelse af login-mails). Begge er amerikanske selskaber. Databasen ligger i
          Railways europæiske region, men behandlingen kan også ske i USA, og overførslen
          er dækket af EU-Kommissionens standardbestemmelser. Resend er desuden
          certificeret under EU-U.S. Data Privacy Framework.
        </p>
        <p className="small">
          Der er ingen reklame- eller sporingsværktøjer i appen, og der deles ingen
          oplysninger med andre end de to.
        </p>
      </section>

      <section className="card stack stack--tight">
        <h2>Dine rettigheder</h2>
        <p className="small">
          Du har ret til at se, hvad vi har om dig, til at få rettet noget forkert, og til at få
          det slettet. De to første kan du klare selv:
        </p>
        <ul className="small" style={{ margin: '0 0 0 18px', padding: 0 }}>
          <li>
            <strong>Se dine data:</strong> under <Link to="/profil">Profil</Link> → «Hent mine data».
          </li>
          <li>
            <strong>Slet din adgang:</strong> samme sted. Bemærk, at du dermed ikke melder dig ud
            af foreningen — medlemskabet ligger i Conventus.
          </li>
          <li>
            <strong>Rettelser:</strong> navn, mail og holdtilmelding rettes i Conventus, ellers
            bliver de skrevet over ved næste hentning.
          </li>
        </ul>
        <p className="tiny muted mt-2">
          Du kan klage til Datatilsynet, hvis du mener, vi behandler dine oplysninger forkert.
        </p>
      </section>

      {/*
        DATOEN ER IKKE PYNT. Den er det, der gør teksten til foreningens
        politik frem for et oplæg — og den skal passe med referatet, for det
        er referatet, der dokumenterer vedtagelsen. Ændres teksten på siden
        væsentligt, skal politikken vedtages igen, og datoen her rettes.
      */}
      <p className="tiny muted">Vedtaget af bestyrelsen: 5. oktober 2026.</p>
    </div>
  );
}
